import React from 'react';
import Helmet from 'react-helmet';
import { Archive } from 'react-feather';
import Translate from '@spordle/intl-elements';

import { useTeam } from '../queries/teams';
import { useTeamGamesView } from '../queries/games';
import { SKATER, GOALIE } from '../lib';

import Widget from '../components/Widget';
import Pagination from '../components/Pagination'
import { useConfig } from '../components/ConfigContext';
import { Logo } from '../components/Team';
import Loader from '../components/Loader';
import { BackButton } from '../components/BackButton';
import TeamStar from '../components/MyTeam/TeamStar';
import TeamFilters from '../components/TeamFilters';
import GameList from '../components/GameList';
import Stats from '../components/Stats';
import Standings from '../components/Standings';
import Sponsors from '../components/Sponsors';
import { TeamGameViewButton } from '../components/TeamGameViewButton';

export const TeamHeader = ({ team }) => {
  if (!team) return null;

  return <div className="page-header team-header row d-flex flex-nowrap align-items-center">
    <Logo team={team} />
    <div className="ml-3">
      <h1>{team.name}</h1>
      <h5 className="text-muted">{team.seasonId} <TeamStar team={team} /></h5>
    </div>
  </div>;
}

export const TeamLoading = () => {
  return <div className="container team">
    <Loader>
      <Translate id="routes.team.loading" />
    </Loader>
  </div>
}

export const TeamError = ({ error }) => {
  let message;
  const status = error?.error?.statusCode;
  if (status === 403) {
    message = <Translate id="routes.team.not.available" />
  } else if (status === 404) {
    message = <Translate id="routes.team.does.not.exist" />
  } else if (status >= 400 && status < 600) {
    message = <Translate id="routes.team.something.went.wrong.with.code" values={{ status }} />
  } else {
    message = <Translate id="routes.team.something.went.wrong" />
  }

  return <div className="container team">
    <Helmet meta={[{ name: 'robots', content: 'noindex' }]} />
    <h2>Oops...</h2>
    <p>{message}</p>
    <BackButton />
  </div>
}

const TeamGameList = ({ teamId, scheduleId, groupId }) => {
  const { currentState, startDate, queryLimit, buttonText, nextState } = useTeamGamesView({ teamId, scheduleId, groupId });

  return <>
    <Pagination perPage={null}>
      <GameList filters={{ teamId, scheduleId, groupId, startDate }} queryLimit={queryLimit} />
    </Pagination>
    <TeamGameViewButton buttonText={buttonText} currentState={currentState} nextState={nextState} />
  </>
}

const number = participant => participant?.stats?.number || Infinity; // ensure players without jersey numbers get sorted to end instead of start

export const TeamSkaterStats = ({ teamId, scheduleId, groupId }) => {
  const { teamSkaterSorts, teamSkaterOrders } = useConfig();
  const sorts = teamSkaterSorts && Array.isArray(teamSkaterSorts) && teamSkaterSorts.length > 0 ? teamSkaterSorts : ["stats.isAffiliate", number];
  const orders = teamSkaterOrders && Array.isArray(teamSkaterOrders) && teamSkaterOrders.length > 0 ? teamSkaterOrders : ["ASC", "ASC"];

  return <Pagination perPage={null}>
    <Stats participantType={SKATER} isSingle hideRank filters={{ teamId, scheduleId, groupId }} sorts={sorts} orders={orders} />
  </Pagination>
}

export const TeamGoalieStats = ({ teamId, scheduleId, groupId }) => {
  const { teamGoalieSorts, teamGoalieOrders } = useConfig();
  const sorts = teamGoalieSorts && Array.isArray(teamGoalieSorts) && teamGoalieSorts.length > 0 ? teamGoalieSorts : ["stats.isAffiliate", number];
  const orders = teamGoalieOrders && Array.isArray(teamGoalieOrders) && teamGoalieOrders.length > 0 ? teamGoalieOrders : ["ASC", "ASC"];

  return <Pagination perPage={null}>
    <Stats participantType={GOALIE} isSingle hideRank filters={{ teamId, scheduleId, groupId }} sorts={sorts} orders={orders} />
  </Pagination>
}

const TeamStandings = ({ teamId, scheduleId, groupId }) =>
  <Standings filters={{ scheduleId, groupId }} highlightRow={row => row.teamId == teamId} />

const Team = ({ teamId, filters, ...props }) => {
  const { hideStats } = useConfig();
  const { scheduleId, groupId } = filters;

  return <>
    <h4><Translate id="common.lang.calendar" /></h4>
    <Widget gutters>
      <TeamGameList teamId={teamId} scheduleId={scheduleId} groupId={groupId} />
    </Widget>

    {scheduleId &&
      <div className="row">
        {!hideStats && <div className="col col-12 col-lg-6">
          <h4><Translate id="common.lang.skaters" /></h4>
          <Widget gutters>
            <TeamSkaterStats {...props} teamId={teamId} scheduleId={scheduleId} groupId={groupId} />
          </Widget>
          <h4><Translate id="common.lang.goalies" /></h4>
          <Widget gutters>
            <TeamGoalieStats {...props} teamId={teamId} scheduleId={scheduleId} groupId={groupId} />
          </Widget>
        </div>}
        <div className={`col col-12 ${!hideStats && 'col-lg-6'}`}>
          <h4><Translate id="common.lang.standings" /></h4>
          <Widget gutters>
            <TeamStandings {...props} teamId={teamId} scheduleId={scheduleId} groupId={groupId} />
          </Widget>
        </div>
      </div>}
  </>
}

export default ({ match, scheduleId, groupId, ...props }) => {
  const { seasonId } = useConfig();

  const teamId = parseInt(match.params.teamId, 10);
  const { data: team, status, error } = useTeam(teamId);

  if (status === 'loading') return <TeamLoading />
  if (status === 'error') return <TeamError error={error} />

  return <div className="container team">
    {team && <Helmet title={team.name} />}
    <Widget>
      {team && team.seasonId !== seasonId && <div className="alert alert-warning mb-4">
        <Archive size="1rem" className="mr-2" />
        <Translate id="routes.team.from.past.season" />
      </div>}

      <TeamHeader team={team} />
    </Widget>

    <Widget gutters>
      <TeamFilters teamId={teamId} team={team} initialValues={{ scheduleId, groupId }}>
        <Team />
      </TeamFilters>
    </Widget>

    <Sponsors />
  </div>
}
